import { useState, useMemo, useEffect } from 'react';
import { DateHelper } from '@bryntum/schedulerpro';
import { Skeleton } from '@/modern/components/ui/skeleton';
import { Badge } from '@/modern/components/ui/badge';
import { Button } from '@/modern/components/ui/button';
import FilePreview from '@/modern/components/file-preview';
import { useEntityFileContext } from '@/react/components/entityViews/files/context/EntityFileContext';
import { FormattedFile } from '@/react/components/entityViews/files/context/EntityFileContext';
import InfiniteScroll from '@/modern/components/infinite-scroll';
import { isMediaFile } from '@/other/utils/utils';
import { Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import { FileActionsMenu } from '@/react/components/entityViews/files/components/FileActionsMenu';
import ImageAnnotationEditor from '@/react/components/entityViews/files/components/editors/ImageAnnotationEditor';
import { useSwivuBryntumContext } from '@/react/hooks/useSwivuBryntumContext';
import { useFileSpotIncentive } from '@/react/components/entityViews/files/hooks/useFileSpotIncentive';
import { FileDetailModal } from './FileDetailModal/FileDetailModal';

type FileFilter = 'photos' | 'documents';
type ViewMode = 'grid' | 'list';

const ZOOM_LEVELS = [
  { cols: 6, label: 'Smallest' },
  { cols: 5, label: 'Small' },
  { cols: 4, label: 'Medium' },
  { cols: 3, label: 'Large' },
  { cols: 2, label: 'Larger' },
  { cols: 1, label: 'Largest' }
];

interface FileGridViewProps {
  initialFilter?: FileFilter;
  setFileToAnnotate?: (file: FormattedFile | null) => void;
  onNavigationFilesChange?: (files: FormattedFile[]) => void;
}

export const FileGridView = ({ initialFilter, setFileToAnnotate: externalSetFileToAnnotate, onNavigationFilesChange }: FileGridViewProps = {}) => {
  const {
    isFetchingPhotos,
    hasMorePhotos,
    fetchCompanyCamPhotos,
    isFetchingDocuments,
    hasMoreDocuments,
    fetchCompanyCamDocuments,
    companyCamPhotos,
    companyCamDocuments,
    boardFiles,
    totalFilesCount,
    handleFileOpen,
    handleFileDownload,
    deleteFile,
    entityRecord,
    openedFile,
    isFileEditorOpen,
    setIsFileEditorOpen
  } = useEntityFileContext();

  const { project, editorHandler } = useSwivuBryntumContext();

  const [activeFilter, setActiveFilter] = useState<FileFilter>(initialFilter || 'photos');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [zoomLevel, setZoomLevel] = useState<number>(() => {
    const stored = localStorage.getItem('fileGridZoomLevel');
    return stored ? parseInt(stored, 10) : 1;
  });
  const [internalFileToAnnotate, setInternalFileToAnnotate] = useState<FormattedFile | null>(null);

  // Use external setter if provided, otherwise use internal
  const setFileToAnnotate = externalSetFileToAnnotate || setInternalFileToAnnotate;
  const fileToAnnotate = externalSetFileToAnnotate ? null : internalFileToAnnotate;

  const currentCols = ZOOM_LEVELS[zoomLevel].cols;

  const { shouldShowAdjustPoints, handleAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler
  });

  const isFetchingFiles = useMemo(() => {
    if (activeFilter === 'photos') return isFetchingPhotos;
    if (activeFilter === 'documents') return isFetchingDocuments;
    return isFetchingPhotos || isFetchingDocuments;
  }, [activeFilter, isFetchingPhotos, isFetchingDocuments]);

  const hasMoreFiles = useMemo(() => {
    if (activeFilter === 'photos') return hasMorePhotos;
    if (activeFilter === 'documents') return hasMoreDocuments;
    return false;
  }, [activeFilter, hasMorePhotos, hasMoreDocuments]);

  const fetchMoreFiles = async () => {
    if (activeFilter === 'photos' && hasMorePhotos) {
      await fetchCompanyCamPhotos();
    } else if (activeFilter === 'documents' && hasMoreDocuments) {
      await fetchCompanyCamDocuments();
    }
  };

  // Separate board files into images and documents
  const boardFileImages = useMemo(() => boardFiles.filter(isMediaFile), [boardFiles]);
  const boardFileDocuments = useMemo(() => boardFiles.filter(file => !isMediaFile(file)), [boardFiles]);

  // Combine photos: image board files first, then CompanyCam photos
  const allPhotos = useMemo(() => [...boardFileImages, ...companyCamPhotos], [boardFileImages, companyCamPhotos]);

  // Combine documents: non-image board files first, then CompanyCam documents
  const allDocuments = useMemo(() => [...boardFileDocuments, ...companyCamDocuments], [boardFileDocuments, companyCamDocuments]);

  const filteredFiles = useMemo(() => {
    switch (activeFilter) {
      case 'photos':
        return allPhotos;
      case 'documents':
        return allDocuments;
      default:
        return allPhotos;
    }
  }, [activeFilter, allPhotos, allDocuments]);

  useEffect(() => {
    onNavigationFilesChange?.(filteredFiles);
  }, [filteredFiles, onNavigationFilesChange]);

  const filterCounts = useMemo(() => ({
    photos: totalFilesCount - allDocuments.length,
    documents: allDocuments.length
  }), [totalFilesCount, allDocuments.length]);

  const zoomIn = () => {
    if (zoomLevel < ZOOM_LEVELS.length - 1) {
      const newLevel = zoomLevel + 1;
      setZoomLevel(newLevel);
      localStorage.setItem('fileGridZoomLevel', newLevel.toString());
    }
  };

  const zoomOut = () => {
    if (zoomLevel > 0) {
      const newLevel = zoomLevel - 1;
      setZoomLevel(newLevel);
      localStorage.setItem('fileGridZoomLevel', newLevel.toString());
    }
  };

  return (
    <div className="py-4">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        {/* View Mode Toggle */}
        <div className="flex gap-1 border-0 rounded-md p-1">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className="h-8 px-3"
          >
            <i className="fa-solid fa-grid-2 mr-2" />
            Grid
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="h-8 px-3"
          >
            <i className="fa-solid fa-list mr-2" />
            List
          </Button>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 flex-wrap items-center">
          {/* Zoom Controls - Only show in grid view */}
          {viewMode === 'grid' && (
            <div className="inline-flex rounded-md shadow-sm ml-2" role="group">
              <Button
                variant="outline"
                size="icon"
                onClick={zoomOut}
                disabled={zoomLevel === 0}
                title="Zoom out (smaller tiles)"
                className="rounded-r-none h-8 w-8"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={zoomIn}
                disabled={zoomLevel === ZOOM_LEVELS.length - 1}
                title="Zoom in (larger tiles)"
                className="rounded-l-none border-l-0 h-8 w-8"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
          )}
          <Button
            variant={activeFilter === 'photos' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('photos')}
          >
            <i className="fa-solid fa-image mr-2" />
            Photos
            <Badge variant={activeFilter === 'photos' ? 'secondary' : 'muted'} size="xs" className="ml-2 rounded-full px-1 min-w-auto w-auto">
              {filterCounts.photos}
            </Badge>
          </Button>
          <Button
            variant={activeFilter === 'documents' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('documents')}
          >
            <i className="fa-solid fa-file mr-2" />
            Documents
            <Badge variant={activeFilter === 'documents' ? 'secondary' : 'muted'} size="xs" className="ml-2 rounded-full px-1 min-w-auto w-auto">
              {filterCounts.documents}
            </Badge>
          </Button>
        </div>
      </div>

      {isFetchingFiles && filteredFiles.length === 0 && (
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <Skeleton className="w-full h-32 rounded-md" />
              <Skeleton className="w-3/4 h-4 rounded-md" />
            </div>
          ))}
        </div>
      )}

      {!isFetchingFiles && filteredFiles.length === 0 && <div className="text-center py-8 text-muted-foreground">No files found</div>}

      {filteredFiles.length > 0 && (
        <>
          {viewMode === 'grid' ? (
            <InfiniteScroll hasMore={hasMoreFiles} isLoading={isFetchingFiles} next={fetchMoreFiles} threshold={1}>
              <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${currentCols}, minmax(0, 1fr))` }}>
                {filteredFiles.map((file) => (
                  <div key={file.id} className="group relative">
                    <div
                      onClick={() => handleFileOpen(file)}
                      className="block rounded-lg overflow-hidden bg-background border border-border shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
                    >
                      <div className="aspect-square w-full flex items-center justify-center overflow-hidden">
                        <FilePreview
                          file={file}
                          src={file.originalImageUrl || file.thumbnailUrl}
                          className="w-full h-full object-cover"
                          showName={false}
                          fileIconClassName="size-16"
                          fileIconContainerClassName="w-full h-full"
                          openFileOnClick={false}
                          isThumbnail={true}
                        />
                      </div>

                      {/* Hover overlay with filename and actions */}
                      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-2">
                        <div className="flex justify-end">
                          <FileActionsMenu
                            file={file}
                            onFileOpen={handleFileOpen}
                            onFileDownload={handleFileDownload}
                            onFileAnnotate={setFileToAnnotate}
                            onFileDelete={deleteFile}
                            shouldShowAdjustPoints={shouldShowAdjustPoints}
                            onAdjustPoints={handleAdjustPoints}
                          />
                        </div>
                        <div className="text-white text-sm font-medium truncate" title={file.name}>
                          {file.name}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {hasMoreFiles && (
                <div className="flex justify-center py-4">
                  {isFetchingFiles && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
                </div>
              )}
            </InfiniteScroll>
          ) : (
            <div className="space-y-2">
              <InfiniteScroll hasMore={hasMoreFiles} isLoading={isFetchingFiles} next={fetchMoreFiles} threshold={0.5}>
                {filteredFiles.map((file) => (
                  <a
                    key={file.id}
                    href={file.originalImageUrl || file.thumbnailUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors group"
                  >
                    <div className="flex-shrink-0">
                      <img
                        src={file.thumbnailUrl || file.originalImageUrl || "https://www.iconpacks.net/icons/2/free-file-icon-1453-thumb.png"}
                        alt={file.name}
                        className="w-12 h-12 rounded object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "https://www.iconpacks.net/icons/2/free-file-icon-1453-thumb.png";
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors" title={file.name}>
                        {file.name || 'Untitled'}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                        {file.addedBy && (
                          <span className="flex items-center gap-1">
                            <i className="fa-solid fa-user text-[10px]" />
                            {file.addedBy}
                          </span>
                        )}
                        {file.size && file.size !== "N/A" && <span>{file.size}</span>}
                        {file.lastUpdatedAt && (
                          <span>{DateHelper.format(new Date(file.lastUpdatedAt), 'MM/DD/YYYY')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="fa-solid fa-external-link text-muted-foreground" />
                    </div>
                  </a>
                ))}
                {(isFetchingFiles || hasMoreFiles) && (
                  <div className="flex justify-center py-8">
                    {isFetchingFiles && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
                  </div>
                )}
              </InfiniteScroll>
            </div>
          )}
        </>
      )}
      {/* Render modals only if using internal state (not passed from parent) */}
      {!externalSetFileToAnnotate && (
        <>
          {openedFile && (
            <FileDetailModal
              entityRecord={entityRecord}
              file={openedFile}
              navigationFiles={filteredFiles}
              open={isFileEditorOpen}
              setOpen={setIsFileEditorOpen}
            />
          )}
          {/* Image Annotation Editor */}
          {fileToAnnotate && fileToAnnotate.originalImageUrl && (
            <ImageAnnotationEditor
              project={project}
              entityRecord={entityRecord}
              fileToAnnotate={fileToAnnotate}
              onClose={() => setFileToAnnotate(null)}
            />
          )}
        </>
      )}
    </div>
  );
};
